import { isUnlimited } from '~/utils/quotaConstants'
import { formatDate } from '~/utils/timezone'

interface LicenseMetadataDetailsProps {
  selectedLicenseDetails: any
}

export const LicenseMetadataDetails = ({ selectedLicenseDetails }: LicenseMetadataDetailsProps) => {
  if (!selectedLicenseDetails?.instance_metadata) return null

  const meta = selectedLicenseDetails.instance_metadata as Record<string, unknown>
  const usageQuotas = meta.usage_quotas as
    | Record<string, { limit_value: number; reset_interval: string }>
    | undefined

  return (
    <div className='border rounded-lg p-3'>
      <h4 className='font-semibold text-gray-700 mb-2'>Additional Details</h4>
      <div className='grid grid-cols-2 gap-2'>
        {meta.created_time ? (
          <div>
            <span className='text-gray-500'>Created:</span> {formatDate(String(meta.created_time))}
          </div>
        ) : null}
        {meta.updated_time ? (
          <div>
            <span className='text-gray-500'>Updated:</span> {formatDate(String(meta.updated_time))}
          </div>
        ) : null}
        {meta.note ? (
          <div className='col-span-2'>
            <span className='text-gray-500'>Note:</span>{' '}
            <span className='text-gray-700'>{String(meta.note)}</span>
          </div>
        ) : null}
        {meta.checkout_session_id ? (
          <div className='col-span-2'>
            <span className='text-gray-500'>Checkout Session:</span>{' '}
            <span className='font-mono text-xs'>{String(meta.checkout_session_id)}</span>
          </div>
        ) : null}
        {meta.price_id ? (
          <div className='col-span-2'>
            <span className='text-gray-500'>Stripe Price ID:</span>{' '}
            <span className='font-mono text-xs'>{String(meta.price_id)}</span>
          </div>
        ) : null}
        {meta.payment_link_id ? (
          <div className='col-span-2'>
            <span className='text-gray-500'>Payment Link ID:</span>{' '}
            <span className='font-mono text-xs'>{String(meta.payment_link_id)}</span>
          </div>
        ) : null}
        {meta.upgrade_from_license_id ? (
          <div>
            <span className='text-gray-500'>Upgraded From:</span>{' '}
            <span className='font-mono'>License #{String(meta.upgrade_from_license_id)}</span>
          </div>
        ) : null}
        {meta.downgrade_from_license_id ? (
          <div>
            <span className='text-gray-500'>Downgraded From:</span>{' '}
            <span className='font-mono'>License #{String(meta.downgrade_from_license_id)}</span>
          </div>
        ) : null}
        {meta.renewed_from_license_id ? (
          <div>
            <span className='text-gray-500'>Renewed From:</span>{' '}
            <span className='font-mono'>License #{String(meta.renewed_from_license_id)}</span>
          </div>
        ) : null}
      </div>

      {(meta.upgraded_from || meta.upgraded_at || meta.carryover_debt !== undefined) && (
        <div className='mt-3 pt-3 border-t'>
          <h5 className='font-medium text-gray-600 mb-2 text-xs uppercase'>Upgrade Info</h5>
          <div className='grid grid-cols-2 gap-2'>
            {meta.upgraded_from ? (
              <div>
                <span className='text-gray-500'>Upgraded From Plan:</span>{' '}
                <span className='font-semibold capitalize text-blue-600'>
                  {String(meta.upgraded_from)}
                </span>
              </div>
            ) : null}
            {meta.upgraded_at ? (
              <div>
                <span className='text-gray-500'>Upgraded At:</span>{' '}
                {formatDate(String(meta.upgraded_at))}
              </div>
            ) : null}
            {typeof meta.carryover_debt === 'number' && meta.carryover_debt > 0 && (
              <div>
                <span className='text-gray-500'>Carryover Debt:</span>{' '}
                <span className='font-semibold text-orange-600'>
                  {meta.carryover_debt.toLocaleString()} credits
                </span>
              </div>
            )}
            {meta.debt_carryover_timestamp ? (
              <div>
                <span className='text-gray-500'>Debt Timestamp:</span>{' '}
                {formatDate(String(meta.debt_carryover_timestamp))}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {usageQuotas && Object.keys(usageQuotas).length > 0 && (
        <div className='mt-3 pt-3 border-t'>
          <h5 className='font-medium text-gray-600 mb-2 text-xs uppercase'>Usage Quotas</h5>
          <div className='grid grid-cols-1 gap-2'>
            {Object.entries(usageQuotas).map(([key, value]) => (
              <div
                key={key}
                className='flex justify-between items-center bg-gray-50 rounded px-2 py-1'
              >
                <span className='text-gray-600 capitalize'>{key.replace(/_/g, ' ')}:</span>
                <span className='font-mono'>
                  {isUnlimited(value.limit_value) ? (
                    <span className='text-green-600'>Unlimited</span>
                  ) : (
                    <span className='font-semibold'>{value.limit_value.toLocaleString()}</span>
                  )}
                  <span className='text-gray-400 text-xs ml-2'>({value.reset_interval})</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <details className='mt-3 pt-3 border-t'>
        <summary className='text-xs text-gray-500 cursor-pointer hover:text-gray-700'>
          View Raw Metadata (JSON)
        </summary>
        <pre className='mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto max-h-[200px]'>
          {JSON.stringify(selectedLicenseDetails.instance_metadata, null, 2)}
        </pre>
      </details>
    </div>
  )
}
